/**
 * SEO Panel — per-page SEO metadata editor (title, description, OG image, canonical URL).
 * Renders into the Properties tab when no section is selected.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';

export class SEOPanel {
  constructor(container) {
    this._container = container;
    this._destroyed = false;
    this._render();
    this._listen();
  }

  _getActivePage() {
    const state = store.getState();
    return state.pages.find((p) => p.id === state.activePage) || state.pages[0];
  }

  _getPageIndex() {
    const state = store.getState();
    return state.pages.findIndex((p) => p.id === state.activePage);
  }

  _render() {
    const page = this._getActivePage();
    if (!page) return;
    const seo = page.seo || {};

    const descLen = (seo.description || '').length;
    const descClass = descLen > 160 ? 'pb-seo-char-over' : '';

    this._container.innerHTML = `
      <div class="pb-props-section pb-seo-panel">
        <button type="button" class="pb-props-section-title pb-collapsible" data-pb-collapse="seo" aria-expanded="true" aria-controls="pb-collapse-seo">
          SEO
          <svg class="pb-collapse-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <div class="pb-collapsible-body" data-pb-collapse-body="seo" id="pb-collapse-seo">
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-seo-title">Page Title</label>
            <input type="text" class="pb-input" id="pb-seo-title" value="${this._escAttr(seo.title)}" placeholder="${this._escAttr(page.name)}" spellcheck="false" />
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-seo-desc">Meta Description</label>
            <textarea class="pb-input pb-seo-desc" id="pb-seo-desc" rows="3" placeholder="Brief page description for search engines" spellcheck="false">${this._escHTML(seo.description)}</textarea>
            <span class="pb-seo-char-count ${descClass}" id="pb-seo-char-count">${descLen}/160</span>
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-seo-og">OG Image</label>
            <div class="pb-seo-og-row">
              <input type="text" class="pb-input pb-seo-og-input" id="pb-seo-og" value="${this._escAttr(seo.ogImage)}" placeholder="URL or click Browse" spellcheck="false" />
              <button class="pb-btn pb-btn-sm pb-btn-outline" id="pb-seo-og-browse">Browse</button>
            </div>
            ${seo.ogImage ? `<div class="pb-seo-og-preview" id="pb-seo-og-preview"></div>` : ''}
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-seo-canonical">Canonical URL</label>
            <input type="text" class="pb-input" id="pb-seo-canonical" value="${this._escAttr(seo.canonicalUrl)}" placeholder="https://example.com/page" spellcheck="false" />
          </div>
        </div>
      </div>
    `;

    this._bindEvents();
    if (seo.ogImage) this._showOgPreview(seo.ogImage);
  }

  _bindEvents() {
    const title = this._container.querySelector('#pb-seo-title');
    const desc = this._container.querySelector('#pb-seo-desc');
    const og = this._container.querySelector('#pb-seo-og');
    const ogBrowse = this._container.querySelector('#pb-seo-og-browse');
    const canonical = this._container.querySelector('#pb-seo-canonical');
    const charCount = this._container.querySelector('#pb-seo-char-count');
    const collapseTitle = this._container.querySelector('[data-pb-collapse="seo"]');

    // Collapsible toggle
    if (collapseTitle) {
      collapseTitle.addEventListener('click', () => {
        const body = this._container.querySelector('[data-pb-collapse-body="seo"]');
        if (body) {
          body.classList.toggle('pb-collapsed');
          collapseTitle.classList.toggle('pb-collapsed');
          const expanded = !body.classList.contains('pb-collapsed');
          collapseTitle.setAttribute('aria-expanded', String(expanded));
        }
      });
    }

    // Transaction-based undo batching for text fields
    this._bindTextField(title, 'title');
    this._bindTextField(canonical, 'canonicalUrl');

    // Description with char count
    if (desc) {
      desc.addEventListener('focus', () => store.beginTransaction('Edit SEO description'));
      desc.addEventListener('blur', () => store.commitTransaction());
      desc.addEventListener('input', () => {
        const len = desc.value.length;
        if (charCount) {
          charCount.textContent = `${len}/160`;
          charCount.classList.toggle('pb-seo-char-over', len > 160);
        }
        this._updateSeoField('description', desc.value);
      });
    }

    // OG image text input
    if (og) {
      og.addEventListener('focus', () => store.beginTransaction('Edit OG image'));
      og.addEventListener('blur', () => store.commitTransaction());
      og.addEventListener('input', () => {
        this._updateSeoField('ogImage', og.value);
      });
    }

    // OG image file picker
    if (ogBrowse) {
      ogBrowse.addEventListener('click', () => this._pickOgImage());
    }
  }

  _bindTextField(input, field) {
    if (!input) return;
    input.addEventListener('focus', () => store.beginTransaction(`Edit SEO ${field}`));
    input.addEventListener('blur', () => store.commitTransaction());
    input.addEventListener('input', () => {
      this._updateSeoField(field, input.value);
    });
  }

  _updateSeoField(field, value) {
    const pageIdx = this._getPageIndex();
    if (pageIdx < 0) return;
    store.update(`pages.${pageIdx}.seo.${field}`, value, `Update SEO ${field}`);
  }

  /**
   * Browse for the social card.
   *
   * This was a bare `<input type="file">` for its whole life — the ONE image
   * slot in the builder that never reached the picker. That cost it the library,
   * the stock sources, the brand kits and the drop target, and it is why the
   * placeholder still reads "URL or click Browse": typing a URL by hand was the
   * only way to use anything you had not just picked off your disk. The picker's
   * own Upload tile covers the file case, so nothing is lost by the swap.
   *
   * `purpose: 'og'` narrows the brand-kit grid to `og_blank`, which is the only
   * kit asset with the ~1.91:1 proportion a social card needs. Offering a 1024²
   * logo here would produce a card every scraper letterboxes.
   */
  async _pickOgImage(deps = {}) {
    // Injected for tests, dynamically imported otherwise: the picker pulls in
    // every adapter, and the SEO panel renders on every page selection.
    const showImagePicker =
      deps.showImagePicker ||
      (await import('../editing/image-picker.js').then((m) => m.showImagePicker));
    await showImagePicker(
      async ({ file }) => {
        try {
          if (!file) throw new Error('That image could not be downloaded.');
          const assetId = await store.assets.store(file);
          const assetUri = `pb-asset://${assetId}`;
          this._updateSeoField('ogImage', assetUri);
          const ogInput = this._container.querySelector('#pb-seo-og');
          if (ogInput) ogInput.value = assetUri;
          this._showOgPreview(assetUri);
        } catch (err) {
          // REPORTED here and RETHROWN, and both halves are load-bearing.
          //
          // `applyPick` handles a throwing `onSelect` by marking the card and
          // calling `reportError('image-picker', err, {user: 'silent'})` — no
          // toast, and a scope that names the dialog rather than the field the
          // user was filling. Relying on it alone silently downgrades the
          // guarantee this panel has always made: a failed store REACHES the
          // user. The rethrow is what keeps the picker open with the failure on
          // the card that caused it, so the two channels say different halves of
          // the same thing rather than duplicating one.
          //
          // Same shape as `pwa-install-settings.js pickIconFromBrandKit`, which
          // records on its own panel and rethrows for exactly this reason.
          reportError('SEO OG image', err, {
            user: 'toast',
            message: 'Could not save that image. Try a different one.'
          });
          throw err;
        }
      },
      {
        purpose: 'og',
        noun: 'Social card',
        context: 'Filling the social preview image',
        currentImageUrl: this._getActivePage()?.seo?.ogImage || ''
      }
    ).catch((err) => {
      // Only reached by a failure OUTSIDE the pick loop — the dialog itself
      // failing to open. A per-pick failure is already reported above.
      reportError('SEO OG image', err, {
        user: 'toast',
        message: 'The image picker could not be opened.'
      });
    });
  }

  async _showOgPreview(uri) {
    let previewEl = this._container.querySelector('#pb-seo-og-preview');
    if (!previewEl) {
      // Create preview container if it doesn't exist
      const ogRow = this._container.querySelector('.pb-seo-og-row');
      if (!ogRow) return;
      previewEl = document.createElement('div');
      previewEl.className = 'pb-seo-og-preview';
      previewEl.id = 'pb-seo-og-preview';
      ogRow.parentElement.appendChild(previewEl);
    }

    if (!uri) {
      previewEl.innerHTML = '';
      return;
    }

    let src = uri;
    if (uri.startsWith('pb-asset://')) {
      const assetId = uri.replace('pb-asset://', '');
      try {
        src = await store.assets.getBlobUrl(assetId);
      } catch {
        previewEl.innerHTML = '<span class="pb-seo-og-missing">Image not found</span>';
        return;
      }
    }

    previewEl.innerHTML = `<img src="${this._escAttr(src)}" alt="OG Image preview" style="max-width:100%;max-height:80px;border-radius:4px;margin-top:4px;" />`;
  }

  _listen() {
    this._trackedPageId = store.getState().activePage;

    this._onStateChanged = () => {
      if (this._destroyed) return;
      const state = store.getState();
      // Full re-render if active page changed (page switch, undo/redo)
      if (state.activePage !== this._trackedPageId) {
        this._trackedPageId = state.activePage;
        this._render();
        return;
      }
      // Otherwise sync field values without full re-render (preserves focus)
      this._syncFields();
    };
    eventBus.on('state-changed', this._onStateChanged);
  }

  _syncFields() {
    const page = this._getActivePage();
    if (!page) return;
    const seo = page.seo || {};

    const title = this._container.querySelector('#pb-seo-title');
    const desc = this._container.querySelector('#pb-seo-desc');
    const og = this._container.querySelector('#pb-seo-og');
    const canonical = this._container.querySelector('#pb-seo-canonical');
    const charCount = this._container.querySelector('#pb-seo-char-count');

    // Only update if the input isn't currently focused (avoid clobbering user typing)
    if (title && document.activeElement !== title) title.value = seo.title || '';
    if (desc && document.activeElement !== desc) desc.value = seo.description || '';
    if (og && document.activeElement !== og) og.value = seo.ogImage || '';
    if (canonical && document.activeElement !== canonical) canonical.value = seo.canonicalUrl || '';

    if (charCount) {
      const len = (seo.description || '').length;
      charCount.textContent = `${len}/160`;
      charCount.classList.toggle('pb-seo-char-over', len > 160);
    }
  }

  focusField(fieldKey = 'title') {
    const selectors = {
      title: '#pb-seo-title',
      description: '#pb-seo-desc',
      ogImage: '#pb-seo-og',
      canonicalUrl: '#pb-seo-canonical',
      canonical: '#pb-seo-canonical'
    };
    const input = this._container.querySelector(selectors[fieldKey] || selectors.title);
    if (!input) return;
    input.focus({ preventScroll: false });
    if (typeof input.select === 'function') input.select();
  }

  destroy() {
    this._destroyed = true;
    // Force-commit any open transaction
    store.commitTransaction();
    eventBus.off('state-changed', this._onStateChanged);
  }

  _escAttr(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  _escHTML(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
